import React, { useId } from 'react';
import type { PublicRfiProjection, PublicRfiItemState } from '@find-my-part/types';
import { PUBLIC_RFI_STATE_LABELS } from '@find-my-part/types';
import { useStyleInjection } from '../../hooks';
import { formatTimeAgo } from '../../lib/formatTimeAgo';
import { BREAKPOINTS } from '../../tokens';

export interface RfiStillNeededProps {
  /** Display-safe public RFI projection (PR D), or `null` when none exists. */
  rfi: PublicRfiProjection | null;
}

/**
 * Single source of truth for RFI state presentation. Badge text AND color both
 * come from this one map so they can never drift. State is conveyed by the
 * human-readable label (never color alone — WCAG: text, not color).
 *
 * Mirrors the worker-side public label map (PR D, ADR-020 §13): the warning
 * treatment flags states that still need poster action; the success treatment
 * flags states where information has been received.
 */
const RFI_STATE_DISPLAY: Record<PublicRfiItemState, { label: string; badgeClass: string }> = {
  STILL_NEEDED: {
    label: PUBLIC_RFI_STATE_LABELS.STILL_NEEDED,
    badgeClass: 'fmp-rfi__badge--warning',
  },
  INFO_RECEIVED: {
    label: PUBLIC_RFI_STATE_LABELS.INFO_RECEIVED,
    badgeClass: 'fmp-rfi__badge--success',
  },
  MORE_DETAIL_NEEDED: {
    label: PUBLIC_RFI_STATE_LABELS.MORE_DETAIL_NEEDED,
    badgeClass: 'fmp-rfi__badge--warning',
  },
};

/**
 * Renders PR D's already-shipped public RFI projection (`data.rfi`) as a
 * display-only "still needed" block on the question-detail view. Read-only:
 * no fetch, no mutation — it renders exactly what the projection contains and
 * degrades to nothing when `rfi` is `null` or empty.
 *
 * Workflow content, visually/semantically distinct from the green
 * "Best Answer"/"Solution" treatment so a visitor never confuses "needs info"
 * with "answered". All staff prose (`message`/`label`/`heading`) renders as
 * React text children (auto-escaped) — never `dangerouslySetInnerHTML`.
 */
export function RfiStillNeeded({ rfi }: RfiStillNeededProps): React.ReactElement | null {
  // Rules-of-hooks: ALL hooks run unconditionally, BEFORE the null return.
  useStyleInjection(rfiStillNeededStyles, 'fmp-rfi-still-needed-styles');
  const headingId = useId();

  // PR D guarantees ≥1 item per returned request (and `null` when all are
  // empty). We still filter defensively so the component fully owns its
  // "render nothing or render something meaningful" contract: a degenerate
  // all-itemless projection degrades to nothing rather than a heading-only
  // section.
  const renderableRequests =
    rfi === null ? [] : rfi.requests.filter((request) => request.items.length > 0);

  if (rfi === null || renderableRequests.length === 0) return null;

  return (
    <section className="fmp-rfi" aria-labelledby={headingId}>
      <h3 id={headingId} className="fmp-rfi__heading">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className="fmp-rfi__icon"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        {rfi.heading}
      </h3>

      {/* role="list" kept alongside the inner <ul>: both lists set
          list-style: none, which Safari/VoiceOver treat as a cue to strip the
          implicit list role — the role restores list semantics for both. */}
      <ol className="fmp-rfi__requests" role="list">
        {renderableRequests.map((request, requestIndex) => {
          const requestDisplay = RFI_STATE_DISPLAY[request.state];

          return (
            // No stable id is exposed by the display-safe projection; these
            // lists are static (never reordered), so an index key is correct.
            <li key={requestIndex} className="fmp-rfi__request">
              <div className="fmp-rfi__request-header">
                <span className={`fmp-rfi__badge ${requestDisplay.badgeClass}`}>
                  {requestDisplay.label}
                </span>
                <time
                  className="fmp-rfi__time"
                  dateTime={new Date(request.requestedAt).toISOString()}
                >
                  {formatTimeAgo(request.requestedAt)}
                </time>
              </div>

              <p className="fmp-rfi__message">{request.message}</p>

              <ul className="fmp-rfi__items" role="list">
                {request.items.map((item, itemIndex) => {
                  const itemDisplay = RFI_STATE_DISPLAY[item.state];

                  return (
                    <li key={itemIndex} className="fmp-rfi__item">
                      <span className="fmp-rfi__item-label">
                        {item.label}
                        {!item.required && (
                          <span className="fmp-rfi__item-optional"> (optional)</span>
                        )}
                      </span>
                      <span className={`fmp-rfi__badge ${itemDisplay.badgeClass}`}>
                        {itemDisplay.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const rfiStillNeededStyles = `
  :where(.fmp-widget-scope) .fmp-rfi {
    margin: 0 var(--fmp-space-xl) var(--fmp-space-lg);
    padding: var(--fmp-space-lg);
    background: var(--fmp-color-info-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-info-border);
    border-radius: var(--fmp-radius-surface);
  }

  :where(.fmp-widget-scope) .fmp-rfi__heading {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    margin: 0 0 var(--fmp-space-md);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-rfi__icon {
    width: var(--fmp-size-18);
    height: var(--fmp-size-18);
    flex-shrink: 0;
    color: var(--fmp-color-primary);
  }

  :where(.fmp-widget-scope) .fmp-rfi__requests {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-rfi__request {
    padding: var(--fmp-space-md);
    background: var(--fmp-color-surface);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
  }

  :where(.fmp-widget-scope) .fmp-rfi__request-header {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    flex-wrap: wrap;
    margin-bottom: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-rfi__time {
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-rfi__message {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0 0 var(--fmp-space-md);
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-secondary);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  :where(.fmp-widget-scope) .fmp-rfi__items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-rfi__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--fmp-space-sm);
    flex-wrap: wrap;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-rfi__item-label {
    max-inline-size: var(--fmp-layout-prose-max);
    flex: 1 1 auto;
    overflow-wrap: anywhere;
  }

  :where(.fmp-widget-scope) .fmp-rfi__item-optional {
    color: var(--fmp-color-text-muted);
    font-style: italic;
  }

  :where(.fmp-widget-scope) .fmp-rfi__badge {
    display: inline-block;
    flex-shrink: 0;
    padding: var(--fmp-size-2) var(--fmp-space-sm);
    border-radius: var(--fmp-radius-sm);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-rfi__badge--warning {
    background: var(--fmp-color-warning-bg);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-rfi__badge--success {
    background: var(--fmp-color-success-bg);
    color: var(--fmp-color-success);
  }

  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-rfi {
      margin-left: var(--fmp-space-lg);
      margin-right: var(--fmp-space-lg);
    }
  }

  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-rfi,
    :where(.fmp-widget-scope) .fmp-rfi__request {
      border-width: var(--fmp-border-width-strong);
    }
  }
`;
